import React, { useContext, useEffect } from "react"

import {
  faLock,
  faLockOpen,
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import classNames from "src/classNames"
import { findGeminiLanguage } from "src/geminiLanguageOptions"

import CloudroomContext from "components/ChimeCloudroom/CloudroomContext"
import BaseButton from "components/ChimeCloudroom/Shared/BaseButton"
import RemoteScreenShare from "components/ChimeCloudroom/TutorLayout/RemoteScreenShare"

import ToggleTranslationButton from "../TutorLayout/ToggleTranslationButton"
import { setUpVideoElement } from "../cloudroomHelpers/chimeAudioVideoHelpers"
import useVolumeIndicator from "../hooks/useVolumeIndicator"

export type RemoteTileProps = {
  attendeeId: string
  numAttendees?: number
  tutorView?: boolean
  qualityControlView?: boolean
}

const RemoteTile: React.FC<RemoteTileProps> = ({
  attendeeId,
  numAttendees = 1,
  tutorView = false,
  qualityControlView = false,
}) => {
  const {
    session,
    audioVideo,
    allControlsLocked,
    controlsLockedUserIds,
    roster,
    channel,
    getInfoForExternalUserId,
    localUser,
  } = useContext(CloudroomContext)

  const attendee = roster[attendeeId]
  const attendeeInfo = getInfoForExternalUserId?.(attendee?.externalUserId)
  const attendeeVideoOn = attendee?.sharingVideo
  const attendeeScreensharing = attendee?.sharingContent

  const [userType, userId] = attendee?.externalUserId.split("-") ?? []
  const lowBandwidthStudent =
    userType === "Student" && session.lowBandwidthCloudroom
  const remoteControls = tutorView && userType === "Student"

  const initialAudioMuted = session.audioMutedUserIds.includes(userId)

  const { muted } = useVolumeIndicator(
    attendeeId,
    initialAudioMuted,
    audioVideo
  )

  useEffect(() => {
    if (!attendee?.videoTileId) return

    setUpVideoElement({
      audioVideo,
      local: false,
      attendeeId,
      tileId: attendee.videoTileId,
    })
  }, [audioVideo, attendeeId, attendee?.videoTileId])

  const languageLabel = findGeminiLanguage(
    attendeeInfo?.defaultLiveTranslationLanguageCode
  )?.label

  let heightClassName
  if (numAttendees < 2) heightClassName = "max-h-screen"
  else if (numAttendees < 7) heightClassName = "max-h-[50vh]"
  else heightClassName = "max-h-[33vh]"

  return (
    <div
      className={classNames(
        "space-around flex flex-col justify-center",
        heightClassName
      )}
    >
      <div
        className={classNames(
          "flex aspect-video justify-center rounded-lg bg-neutral-800",
          tutorView && "max-h-[calc(100%-100px)]"
        )}
      >
        <video
          id={`video-${attendeeId}`}
          className={classNames(
            "aspect-video max-w-full rounded-lg object-contain",
            !attendeeVideoOn && !attendeeScreensharing && "hidden",
            qualityControlView && "max-h-[19vh]"
          )}
        />
        <div
          className={classNames(
            "flex aspect-video h-full items-center justify-center",
            tutorView ? "text-[max(1.5rem,2cqw)]" : "text-[2cqw]",
            (attendeeVideoOn || attendeeScreensharing) && "hidden",
            qualityControlView && "max-h-[19vh]"
          )}
        >
          {attendeeInfo?.fullName}
          &nbsp;
          {!remoteControls && (
            <FontAwesomeIcon icon={muted ? faMicrophoneSlash : faMicrophone} />
          )}
        </div>
      </div>

      <div className="flex h-20 flex-col items-center py-2">
        {remoteControls && (
          <div className="flex max-w-[500px] justify-between">
            <BaseButton
              onClick={() => {
                if (muted)
                  channel.perform("unmute_user", { type: "audio", userId })
                else channel.perform("mute_user", { type: "audio", userId })
              }}
              active={muted}
              className="mr-4 w-12 py-2"
              activeIcon={faMicrophoneSlash}
              inactiveIcon={faMicrophone}
            />
            {!lowBandwidthStudent && (
              <BaseButton
                onClick={() => {
                  if (attendeeVideoOn)
                    channel.perform("mute_user", { type: "video", userId })
                  else channel.perform("unmute_user", { type: "video", userId })
                }}
                active={!attendeeVideoOn}
                activeIcon={faVideoSlash}
                inactiveIcon={faVideo}
                className="mr-4 w-12 py-2"
              />
            )}
            <BaseButton
              onClick={() => {
                if (controlsLockedUserIds.includes(userId))
                  channel.perform("unlock_user", { userId })
                else channel.perform("lock_user", { userId })
              }}
              active={
                allControlsLocked || controlsLockedUserIds.includes(userId)
              }
              activeIcon={faLock}
              inactiveIcon={faLockOpen}
              className="mr-4 w-12 py-2"
            />
            {attendeeInfo?.audioPlaybackRate === 1 &&
              attendeeInfo.defaultLiveTranslationLanguageCode && (
                <ToggleTranslationButton
                  studentSessionId={attendeeInfo.studentSessionId}
                  liveTranslationEnabled={attendeeInfo.liveTranslationEnabled}
                  languageLabel={languageLabel}
                />
              )}
            <RemoteScreenShare userId={userId} />
          </div>
        )}

        {!qualityControlView && attendeeVideoOn && attendeeInfo?.fullName && (
          <>
            <div className="flex items-center justify-center gap-2">
              {attendeeInfo.fullName}
              {!remoteControls && (
                <FontAwesomeIcon
                  icon={muted ? faMicrophoneSlash : faMicrophone}
                />
              )}
            </div>
            {localUser?.type !== "Student" && (
              <>
                {attendeeInfo?.liveTranslationEnabled && (
                  <div className="flex items-center justify-center text-sm italic text-gray-400">
                    {attendeeInfo.firstName} is hearing session audio in{" "}
                    {languageLabel}.
                  </div>
                )}
                {attendeeInfo?.audioPlaybackRate !== 1 && (
                  <div className="flex items-center justify-center text-sm italic text-gray-400">
                    {attendeeInfo.firstName} is hearing session audio at{" "}
                    {attendeeInfo?.audioPlaybackRate}x speed.
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default RemoteTile
